import React, { useMemo } from 'react';

import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Box, Chip, Stack, Tab, Tabs, Typography } from '@mui/material';

import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import { useGetOfferRatePlans } from '~/hooks/apis/reservation/useOfferRatePlans';

import { RATES_AND_INVENTORY_TABS, getRatesAndInventoryTab } from './constants';
import LinkDealOptionsTab from './tabs/link-deal-options/LinkDealOptionsTab';
import RatePlansTab from './tabs/rate-plans/RatePlansTab';
import RoomTypesTab from './tabs/room-types/RoomTypesTab';
import type { RatesAndInventoryTab } from './types';

export default function OfferBuilderRatesAndInventoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const activeTab = getRatesAndInventoryTab(searchParams.get('tab'));

  const { data: offer } = useGetOffer(offerId);
  const vendorAccountId = offer?.vendor_account_id;
  const { data: ratePlans = [] } = useGetOfferRatePlans(vendorAccountId);

  const tabCounts: Partial<Record<RatesAndInventoryTab, number>> = {
    'rate-plans': ratePlans.length,
  };

  function handleTabChange(tab: RatesAndInventoryTab) {
    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set('tab', tab);
    navigate({ search: nextSearchParams.toString() }, { replace: true });
  }

  return (
    <>
      <Helmet>
        <title>Rates and Inventory | Offer Builder</title>
      </Helmet>

      <Typography variant="h4" sx={{ mb: 1 }}>
        Rates and Inventory
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage rate plans, attach them to rooms on this property as room rates, and link deal options to the rate plan
        they sell on.
      </Typography>

      <Tabs value={activeTab} sx={{ mb: 3 }} onChange={(_event, value: RatesAndInventoryTab) => handleTabChange(value)}>
        {RATES_AND_INVENTORY_TABS.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            icon={<tab.icon fontSize="small" />}
            iconPosition="start"
            label={
              tabCounts[tab.value] === undefined ? (
                tab.label
              ) : (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>{tab.label}</span>
                  <Chip label={tabCounts[tab.value]} size="small" />
                </Stack>
              )
            }
          />
        ))}
      </Tabs>

      <Box>
        {activeTab === 'rate-plans' && <RatePlansTab />}
        {activeTab === 'room-types' && <RoomTypesTab />}
        {activeTab === 'link-deal-options' && <LinkDealOptionsTab />}
      </Box>
    </>
  );
}
